import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Plus, Trash2, Loader2, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import { regrasOFXAPI } from '../services/api';

// Categorias disponíveis (mesmas do CaixaPage)
const CLASSIF_ENTRADA = [
  { value: 'recebimento_operacional', label: 'Recebimento de Cliente' },
  { value: 'aporte_capital', label: 'Aporte / Injeção' },
  { value: 'emprestimo_recebido', label: 'Empréstimo Recebido' },
  { value: 'outras_entradas', label: 'Outras Entradas' },
];
const CLASSIF_SAIDA = [
  { value: 'operacional_recorrente', label: 'Custo Fixo / Operacional' },
  { value: 'retirada_recorrente', label: 'Retirada / Pró-labore' },
  { value: 'pontual', label: 'Gasto Pontual' },
  { value: 'parcela_compromisso', label: 'Parcela de Financiamento' },
  { value: 'outras_saidas', label: 'Outras Saídas' },
];

const labelClassificacao = (value) => {
  const all = [...CLASSIF_ENTRADA, ...CLASSIF_SAIDA];
  return all.find((c) => c.value === value)?.label || value;
};

// Gerenciador reutilizável de Regras de Categorização OFX
export const RegrasOFXManager = ({ gymId }) => {
  const [regras, setRegras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [form, setForm] = useState({ texto: '', tipo: 'saida', acao: 'classificar', classificacao: 'operacional_recorrente' });

  const load = useCallback(async () => {
    if (!gymId) return;
    try {
      setLoading(true);
      const res = await regrasOFXAPI.listar(gymId);
      setRegras(res.data || []);
    } catch (e) {
      toast.error('Erro ao carregar regras');
    } finally {
      setLoading(false);
    }
  }, [gymId]);

  useEffect(() => { load(); }, [load]);

  const opcoesClassificacao = form.tipo === 'entrada' ? CLASSIF_ENTRADA
    : form.tipo === 'saida' ? CLASSIF_SAIDA
    : [...CLASSIF_ENTRADA, ...CLASSIF_SAIDA];

  const handleTipoChange = (novoTipo) => {
    const opts = novoTipo === 'entrada' ? CLASSIF_ENTRADA : novoTipo === 'saida' ? CLASSIF_SAIDA : [...CLASSIF_ENTRADA, ...CLASSIF_SAIDA];
    setForm((f) => ({ ...f, tipo: novoTipo, classificacao: opts[0]?.value }));
  };

  const handleCriar = async () => {
    if (!form.texto.trim()) { toast.error('Digite o texto que a regra vai procurar'); return; }
    try {
      setSalvando(true);
      await regrasOFXAPI.criar({
        gym_id: gymId,
        texto: form.texto.trim(),
        tipo: form.tipo,
        acao: form.acao,
        classificacao: form.acao === 'classificar' ? form.classificacao : null,
      });
      toast.success('Regra criada!');
      setForm({ texto: '', tipo: 'saida', acao: 'classificar', classificacao: 'operacional_recorrente' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao criar regra');
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluir = async (id) => {
    try {
      await regrasOFXAPI.excluir(id);
      setRegras((r) => r.filter((x) => x.id !== id));
      toast.success('Regra removida');
    } catch (e) {
      toast.error('Erro ao remover');
    }
  };

  return (
    <div className="space-y-5" data-testid="regras-ofx-manager">
      <div className="flex items-start gap-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/30">
        <Wand2 className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-200/90 leading-relaxed">
          Crie regras para categorizar (ou ignorar) automaticamente lançamentos do extrato pela descrição.
          Ex.: se a descrição contém <strong>"tarifa"</strong> → classificar como <strong>Custo Fixo</strong>.
          As regras têm prioridade máxima sobre a IA e a heurística.
        </p>
      </div>

      {/* Formulário de nova regra */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end p-4 rounded-lg bg-zinc-800/40 border border-zinc-700/60">
        <div className="md:col-span-4">
          <label className="text-xs text-zinc-400 mb-1 block">Se a descrição contém</label>
          <Input
            value={form.texto}
            onChange={(e) => setForm({ ...form, texto: e.target.value })}
            placeholder="ex: tarifa, uber, ifood..."
            className="bg-zinc-900 border-zinc-700 text-white h-10"
            data-testid="regra-texto-input"
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-zinc-400 mb-1 block">Tipo</label>
          <select
            value={form.tipo}
            onChange={(e) => handleTipoChange(e.target.value)}
            className="w-full h-10 px-3 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-emerald-500 focus:outline-none"
            data-testid="regra-tipo-select"
          >
            <option value="saida">Saída</option>
            <option value="entrada">Entrada</option>
            <option value="ambos">Ambos</option>
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs text-zinc-400 mb-1 block">Ação</label>
          <select
            value={form.acao}
            onChange={(e) => setForm({ ...form, acao: e.target.value })}
            className="w-full h-10 px-3 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-emerald-500 focus:outline-none"
            data-testid="regra-acao-select"
          >
            <option value="classificar">Classificar</option>
            <option value="ignorar">Ignorar</option>
          </select>
        </div>
        <div className="md:col-span-3">
          <label className="text-xs text-zinc-400 mb-1 block">Categoria</label>
          <select
            value={form.classificacao}
            onChange={(e) => setForm({ ...form, classificacao: e.target.value })}
            disabled={form.acao === 'ignorar'}
            className="w-full h-10 px-3 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:border-emerald-500 focus:outline-none disabled:opacity-40"
            data-testid="regra-classificacao-select"
          >
            {opcoesClassificacao.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-1">
          <Button
            onClick={handleCriar}
            disabled={salvando}
            className="w-full bg-emerald-600 hover:bg-emerald-700 h-10 px-0"
            data-testid="regra-criar-btn"
          >
            {salvando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Lista de regras */}
      {loading ? (
        <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 text-emerald-400 animate-spin" /></div>
      ) : regras.length === 0 ? (
        <p className="text-sm text-zinc-500 text-center py-6">Nenhuma regra criada ainda.</p>
      ) : (
        <div className="space-y-2" data-testid="regras-lista">
          {regras.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-zinc-800/40 border border-zinc-700/50" data-testid={`regra-item-${r.id}`}>
              <div className="flex items-center gap-2 flex-wrap text-sm min-w-0">
                <span className="text-zinc-400">contém</span>
                <span className="px-2 py-0.5 rounded bg-zinc-700 text-white font-medium">"{r.texto}"</span>
                <span className="text-xs text-zinc-500 uppercase">{r.tipo}</span>
                <span className="text-zinc-500">→</span>
                {r.acao === 'ignorar' ? (
                  <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-xs">Ignorar</span>
                ) : (
                  <span className="px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400 text-xs">{labelClassificacao(r.classificacao)}</span>
                )}
              </div>
              <button
                onClick={() => handleExcluir(r.id)}
                className="text-zinc-500 hover:text-red-400 transition-colors flex-shrink-0"
                data-testid={`regra-excluir-${r.id}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default RegrasOFXManager;
